import { Select } from '@ardoq/select';
import styled from 'styled-components';
import {
  CommonFieldProps,
  FIELD_SELECTOR,
  Field,
  NumberInput,
} from '@ardoq/forms';
import { s8 } from '@ardoq/design-tokens';
import { RelativeDatePickerMode, RelativeDatePickerValue } from './types';
import {
  composeRelativeDate,
  getIsISODateString,
  getRelativeDateOptions,
  isTimeFrameUnit,
} from './utils';
import { noop } from 'lodash';
import { TimeFrameUnit, dissectRelativeDate } from '@ardoq/date-time';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';

type RelativeDatePickerProps = CommonFieldProps & {
  value?: string;
  autoFocus?: boolean;
  onChange?: (value: RelativeDatePickerValue) => void;
  relativeDatePickerMode?: RelativeDatePickerMode;
  menuPortalTarget?: HTMLElement | null;
  i18nLocale?: I18nLocale;
};

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${s8};

  ${FIELD_SELECTOR}:first-child {
    width: 72px;
  }
  ${FIELD_SELECTOR}:last-child {
    flex: 1;
  }
`;

export const RelativeDateInput = ({
  label,
  autoFocus,
  value = '',
  onChange = noop,
  relativeDatePickerMode = RelativeDatePickerMode.DATE_TIME,
  isDisabled,
  menuPortalTarget,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  hasError: hasErrorProp,
  hasWarning,
  ...commonFieldProps
}: RelativeDatePickerProps) => {
  const hasError = Boolean(commonFieldProps.errorMessage) || hasErrorProp;
  const dissectedDate = dissectRelativeDate(value) || {
    unit: TimeFrameUnit.DAY_AGO,
    value: 1,
  };
  return (
    <Field label={label} {...commonFieldProps}>
      <Row>
        <NumberInput
          showUpDownArrows={false}
          hasError={hasError}
          value={dissectedDate.value}
          onValueChange={newValue => {
            onChange(
              composeRelativeDate({
                unit: dissectedDate.unit,
                value: newValue,
              })
            );
          }}
          isDisabled={isDisabled}
          hasWarning={hasWarning}
        />
        <Select
          hasError={hasError}
          autoFocus={autoFocus}
          openMenuOnFocus
          value={dissectedDate.unit}
          options={getRelativeDateOptions(
            dissectedDate.value,
            relativeDatePickerMode,
            i18nLocale
          )}
          onBlur={() =>
            getIsISODateString(value) &&
            onChange(composeRelativeDate(dissectedDate))
          }
          onValueChange={selectValue => {
            if (selectValue && isTimeFrameUnit(selectValue as string)) {
              onChange(
                composeRelativeDate({
                  unit: selectValue as TimeFrameUnit,
                  value: dissectedDate.value,
                })
              );
            } else {
              onChange(selectValue as string);
            }
          }}
          isDisabled={isDisabled}
          hasWarning={hasWarning}
          menuPortalTarget={menuPortalTarget}
        />
      </Row>
    </Field>
  );
};
